import { BENEFIT_LABELS } from "pages/HospitalsPage/components/BenefitSelection/constants";
import { IFallbackCaseRow } from "types/BenefitFallbackCases/BenefitFallbackCases";
import { BenefitFallbackSource } from "types/BenefitFallbacks/BenefitFallbacks";

import { sourceLabel } from "./fallbacksWording";

/**
 * CLE-3047 — the words Eligibility › Fallbacks’ checks list uses for one case. Pure, so they are tested without rendering the
 * table. Service lines and benefits are named exactly as Eligibility › Fallbacks names them (`sourceLabel`,
 * `BENEFIT_LABELS`), so a case reads in the same words as the rule that produced it.
 */

export const benefitInWords = (row: IFallbackCaseRow): string =>
  BENEFIT_LABELS[row.benefit] ?? row.benefit;

/** "Where it came from". */
export const cameFromInWords = (row: IFallbackCaseRow): string => {
  if (row.rung === "HISTORY")
    return "Earlier checks of this plan by other patients";
  return row.source
    ? `Substituted from the ${sourceLabel(
        row.source as BenefitFallbackSource
      )} benefit`
    : "Substituted from another service line's benefit";
};

/** Co-insurance is a whole percent, copays are dollars; null is a blank the estimate kept blank. */
export const valueInWords = (row: IFallbackCaseRow): string => {
  if (row.value === null) return "Blank";
  if (row.benefit === "COINSURANCE_PERCENT") return `${row.value}%`;
  return Number.isInteger(row.value)
    ? `$${row.value}`
    : `$${row.value.toFixed(2)}`;
};

/** "Used on the estimate": the engine's value was the one quoted, or it was only recorded beside legacy's. */
export const usedOnEstimateInWords = (row: IFallbackCaseRow): string =>
  row.authoritative ? "Yes" : "No — shadow only";

/** The estimate the check fed, under the estimate's own account. Null when no estimate used the check. */
export const estimateLink = (row: IFallbackCaseRow): string | null =>
  row.orderId
    ? `/orders/review/${row.orderId}?accountId=${
        row.orderAccountId ?? row.accountId
      }`
    : null;

export const payerPlanInWords = (
  row: IFallbackCaseRow
): { payer: string; plan: string | null } => ({
  payer: row.payerName?.trim() || "—",
  plan: row.planName?.trim() || null,
});

export const checkedAtInWords = (row: IFallbackCaseRow): string =>
  new Date(row.checkedAt).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

/** A UTC calendar day, `YYYY-MM-DD`. */
const utcDay = (date: Date): string => date.toISOString().slice(0, 10);

const DAY_MS = 24 * 60 * 60 * 1000;

export const FALLBACK_CASES_DEFAULT_DAYS = 30;

/**
 * The default window: the last 30 days, ending today — in UTC DAYS, because the API filters on whole UTC days
 * (`from` / `to` are inclusive UTC dates). The Date of check column still shows each check in local time.
 */
export const defaultFallbackCasesRange = (
  now: Date = new Date()
): { from: string; to: string } => ({
  from: utcDay(new Date(now.getTime() - FALLBACK_CASES_DEFAULT_DAYS * DAY_MS)),
  to: utcDay(now),
});
